import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import ROUTES from "../../constants/routes";

import "./PaymentFailedBanner.scss";

/*
 * THE FAILED-PAYMENT GRACE PERIOD: the in-app half, which did not exist.
 *
 * Found 2026-08-30 while walking the untested account states. A failed payment
 * sets `flags.paymentFailedAt` (webhook.ts, `invoice.payment_failed`) and
 * starts a TEN DAY clock; `runNonPaymentSuspensionSweep` suspends the account
 * when it runs out. That much worked.
 *
 * What did not exist was any sign of it in the app. `paymentFailedAt` was never
 * read by the frontend at all - the only account flag the UI touched was
 * `lockDate`. A customer whose card had failed saw a completely normal
 * dashboard for ten days and then, one morning, a suspended account.
 *
 * An email IS sent when the clock starts (FAILDED_PAYMENT, sent from the same
 * webhook). That is not enough on its own to hang someone's account on: it is
 * one message, ten days before the consequence, competing with everything else
 * in an inbox, and it is exactly the kind of mail that gets filtered. The
 * product knows the card failed every single time the customer opens the
 * dashboard, and said nothing.
 *
 * ─── WHY THIS ONE IS NOT DISMISSIBLE ────────────────────────────────────────
 *
 * OverageBanner and AccountIssueBanner are both dismissible until their last,
 * most urgent stage. This one never is. The distinction is what dismissal would
 * cost: an overage banner dismissed too early costs some tracking, and the
 * customer can start a new buffer. A failed payment dismissed on day one ends
 * with a suspended account on day ten, and nothing in between would have
 * reminded them. There is also exactly one action that resolves it, and it
 * takes a minute - so a banner that stays until the card is fixed is
 * proportionate rather than nagging.
 *
 * Per DESIGN_RULES.md, this renders in the ONE system-message region at the top
 * of the page, alongside the other two, rather than inventing a new surface.
 */

/*
 * 🔴 Must match NON_PAYMENT_GRACE_PERIOD_DAYS in
 * SubscriptionApi/Service/account.ts, which is what actually suspends the
 * account. If these disagree, the banner counts down to the wrong day and the
 * customer is suspended either earlier or later than they were told - which is
 * worse than showing no date at all, because they will have planned around it.
 */
const GRACE_PERIOD_DAYS = 10;

const PaymentFailedBanner = () => {
  const authReducer: any = useSelector((state: any) => state?.AuthReducer);
  const flags = authReducer?.accountRecord?.flags;
  const paymentFailedAt: string | null = flags?.paymentFailedAt ?? null;

  /*
   * Once the account is actually suspended the grace period is over and this
   * banner would be telling the customer about a deadline that has passed. The
   * suspended state has its own, blunter treatment, and two messages about one
   * condition is precisely what DESIGN_RULES.md forbids.
   */
  if (!paymentFailedAt || flags?.suspended) {
    return null;
  }

  const failedAt = new Date(paymentFailedAt);
  if (isNaN(failedAt.getTime())) {
    return null;
  }

  const suspendsAt = new Date(failedAt.getTime());
  suspendsAt.setDate(suspendsAt.getDate() + GRACE_PERIOD_DAYS);

  const msLeft = suspendsAt.getTime() - Date.now();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));

  const settingsRoute = ROUTES.SETTINGS.replace(":id", "");

  /*
   * State first, then the consequence, then the date - the same shape the
   * overage banner uses. The date is spelled out rather than left as "in 10
   * days" so it survives being read on any later day, and because a customer
   * deciding when to deal with this needs a date, not an interval.
   */
  const deadline = suspendsAt.toLocaleDateString();

  return (
    <div className="payment-failed-banner" role="alert">
      <p>
        <strong>Your last payment did not go through.</strong>{" "}
        {daysLeft > 0 ? (
          <>
            Tracking continues for now. If the payment is not completed by{" "}
            <strong>{deadline}</strong> ({daysLeft}{" "}
            {daysLeft === 1 ? "day" : "days"} left), your account will be
            suspended and Skydive will stop recording visitors.
          </>
        ) : (
          <>
            Your account is about to be suspended and Skydive will stop
            recording visitors.
          </>
        )}{" "}
        <Link to={settingsRoute}>Update your payment details</Link> to keep it
        running.
      </p>
    </div>
  );
};

export default PaymentFailedBanner;
